import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Box,
  Button,
  IconButton,
  Modal,
  Step as MUIStep,
  Stepper as MUIStepper,
  StepLabel as MUIStepLabel,
  Stack,
  Typography
} from '@mui/material'

import { ActionIcon } from 'components/Modal/ActionModal'

import { OPERATION_FIND_RAN_ROOMS_REMAINING } from '../../operations-gql/queries'
import { getSsidOptions, isByodSsid } from '../../Form/StimeFields'
import { getColumnsRange, getInvalidLineNumbers, parseCsv } from './csv'
import {
  cancelButtonSx,
  confirmButtonSx,
  importModalSx,
  modalBodySx,
  modalFooterSx,
  modalHeaderSx,
  modalPanelSx,
  stepperSx
} from './ImportStyles'
import ImportStepIcon from './ImportStepIcon'
import StepFile from './StepFile'
import StepImport from './StepImport'
import StepPreview from './StepPreview'

const STEP_KEYS = ['ran.import.step1', 'ran.import.step2', 'ran.import.step3']

const Import = () => {
  const [data, setData] = useState('')
  const [file, setFile] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [processing, setProcessing] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { siteId } = useParams()
  const { isStime, ssids } = useOutletContext()
  const ssidOptions = useMemo(() => getSsidOptions(ssids ?? []), [ssids])
  const [ssidId, setSsidId] = useState(() => (ssidOptions.length === 1 ? ssidOptions[0].value : ''))
  const isMissingSsid = isStime && !ssidId
  const withPsk = Boolean(isStime) && !isByodSsid(ssids ?? [], ssidId)
  const { data: remainingKeysData, loading: remainingKeysLoading } = useQuery(OPERATION_FIND_RAN_ROOMS_REMAINING, {
    variables: { sf_id: siteId, ...(isStime && { ssid_id: ssidId }) },
    skip: !siteId || isMissingSsid
  })
  const spacesCount = useMemo(() => parseCsv(data, withPsk).length, [data, withPsk])
  const invalidLineNumbers = useMemo(() => getInvalidLineNumbers(data, withPsk), [data, withPsk])
  const hasColumnsError = invalidLineNumbers.length > 0
  const remainingKeys = remainingKeysData?.count
  const limitExceeded = Number.isFinite(remainingKeys) && spacesCount > remainingKeys

  const getFileError = () => {
    if (hasColumnsError) {
      return t('ran.import.columns_error', {
        count: invalidLineNumbers.length,
        ...getColumnsRange(withPsk),
        lines: invalidLineNumbers.join(', ')
      })
    }
    if (limitExceeded) {
      return t('ran.import.available_keys_error', {
        count: spacesCount,
        available: remainingKeys
      })
    }
    return null
  }

  const isFileStepBlocked = !file || isMissingSsid || remainingKeysLoading || limitExceeded || hasColumnsError

  const handleNextPage = () => {
    if (activeStep === 0 && isFileStepBlocked) {
      return
    }
    if (activeStep === 1) {
      setProcessing(true)
    } else if (activeStep === 2) {
      navigate('..')
      return
    }
    setActiveStep(currentStep => currentStep + 1)
  }

  const handlePreviousPage = () => {
    if (activeStep === 0) {
      navigate('..')
      return
    }
    if (activeStep === 1) {
      setFile(null)
    }
    setData('')
    setProcessing(false)
    setActiveStep(currentStep => currentStep - 1)
  }

  const closeLabel = activeStep > 0 ? t('ran.actions.previous') : t('ran.actions.cancel')
  const confirmLabel = activeStep === 2 ? t('actions.close') : t('ran.actions.next')
  const confirmDisabled = processing || (activeStep === 0 && isFileStepBlocked)

  return (
    <Modal
      open
      onClose={() => navigate('..')}
      sx={importModalSx}>
      <Box sx={modalPanelSx}>
        <Stack sx={modalHeaderSx}>
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <ActionIcon>
                <i className="fa-regular fa-file-arrow-down" />
              </ActionIcon>
              <Typography sx={{ fontSize: 15, lineHeight: 1.4, fontWeight: 600, color: '#292929' }}>
                {t('ran.import.title')}
              </Typography>
            </Stack>
            <IconButton onClick={() => navigate('..')} size="small" sx={{ width: 28, height: 28, color: '#A5A9B3' }}>
              <i className="fa-regular fa-xmark" style={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          <MUIStepper activeStep={activeStep} sx={stepperSx}>
            {STEP_KEYS.map(stepKey => (
              <MUIStep key={stepKey}>
                <MUIStepLabel StepIconComponent={ImportStepIcon}>{t(stepKey)}</MUIStepLabel>
              </MUIStep>
            ))}
          </MUIStepper>
        </Stack>

        <Box sx={modalBodySx}>
          {activeStep === 0 && (
            <StepFile
              ssidOptions={ssidOptions}
              ssidId={ssidId}
              withPsk={withPsk}
              onSsidChange={setSsidId}
              fileError={getFileError()}
              onChange={(newFile, content) => {
                setData(content)
                setFile(newFile)
              }}
            />
          )}
          {activeStep === 1 && <StepPreview data={data} withPsk={withPsk} />}
          {activeStep === 2 && (
            <StepImport
              data={data}
              ssidId={isStime ? ssidId : undefined}
              withPsk={withPsk}
              onFinish={() => {
                setProcessing(false)
              }}
            />
          )}
        </Box>

        <Stack sx={modalFooterSx}>
          {activeStep < 2 && (
            <Button onClick={handlePreviousPage} variant="text" sx={cancelButtonSx}>
              {closeLabel}
            </Button>
          )}
          <Button
            onClick={handleNextPage}
            variant="contained"
            disableElevation
            disabled={confirmDisabled}
            sx={confirmButtonSx}>
            {processing ? <i className="fa-light fa-spinner-third fa-spin" /> : confirmLabel}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}

export default Import
